import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EditPaymentIdentifiers,
  formInputToGqlMutationInput,
  PrimaryFullScreenSpinner,
  SelectItem,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  currencySchema,
  decimalSchema,
  formatLongUnitAmount,
  setPaymentIdentifierSchema,
} from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense, useState } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import type { CustodyAccountRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const formSchema = z.object({
  productId: z.string().min(1, "Required"),
  currency: currencySchema,
  clearingCustodyAccountId: z.string().nullable().optional(),
  transactionFeeRate: decimalSchema.optional(),
  paymentIdentifiers: z
    .array(setPaymentIdentifierSchema)
    .min(1, "At least one payment identifier is required"),
});

type FormData = z.input<typeof formSchema>;

export const Route = createFileRoute("/__auth/custody-accounts")({
  head: () => opsHead("Custody Accounts"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const custodyAccountsDocument = graphql(`
  query AdminCustodyAccounts {
    custodyAccounts {
      ...CustodyAccountRow
    }
    products {
      id
      internalName
      available
    }
  }

  fragment CustodyAccountRow on CustodyAccount {
    id
    shortRef
    currency
    product {
      id
      internalName
    }
    paymentIdentifiers {
      id
    }
    totalBalance {
      amount
      currency
    }
    assignedBalance {
      amount
      currency
    }
    unassignedBalance {
      amount
      currency
    }
    totalUnitBalance {
      amount
      unit
    }
  }
`);

function View() {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(custodyAccountsDocument);

  const custodyAccounts = [...(data.custodyAccounts ?? [])].sort((a, b) =>
    a.shortRef.localeCompare(b.shortRef),
  );

  const availableProducts = [...(data.products ?? [])]
    .filter((product) => product.available)
    .sort((a, b) => a.internalName.localeCompare(b.internalName));

  return (
    <AdminPageContainer testId="admin-custody-accounts">
      <AdminPageHeader
        title="Custody Accounts"
        subtitle="View and create custody accounts"
        buttons={
          <CreateCustodyAccountDialog
            products={availableProducts}
            custodyAccounts={custodyAccounts}
            onCreated={(data) => {
              void navigate({
                to: "/custody-accounts/$id",
                params: { id: data.id },
              });
            }}
          />
        }
      />

      <DataTable
        data={custodyAccounts}
        columns={columns}
        onRowClick={(row) =>
          void navigate({
            to: "/custody-accounts/$id",
            params: { id: row.original.id },
          })
        }
        initialState={{
          pagination: {
            pageSize: 50,
          },
          sorting: [
            {
              id: "shortRef",
              desc: false,
            },
          ],
        }}
        className="overflow-hidden border"
      />
    </AdminPageContainer>
  );
}

function CreateCustodyAccountDialog({
  products,
  custodyAccounts,
  onCreated,
}: {
  products: Array<{ id: string; internalName: string }>;
  custodyAccounts: Array<{
    id: string;
    shortRef: string;
    product?: { internalName: string } | null;
  }>;
  onCreated: (data: { id: string }) => void;
}) {
  const [open, setOpen] = useState(false);

  const [createCustodyAccount, { loading }] = useMutation(
    graphql(`
      mutation AdminCreateCustodyAccount($input: CreateCustodyAccountInput!) {
        createCustodyAccount(input: $input) {
          custodyAccount {
            id
          }
        }
      }
    `),
    {
      onCompleted: (data) => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Custody account created",
        });
        setOpen(false);
        onCreated(data.createCustodyAccount.custodyAccount ?? { id: "" });
      },
      refetchQueries: ["AdminCustodyAccounts"],
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          "Failed to create custody account";

        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating custody account",
          description: errorMessage,
        });
      },
    },
  );

  const form = useAppForm({
    defaultValues: {
      productId: "",
      currency: "AUD",
      clearingCustodyAccountId: null,
      transactionFeeRate: undefined,
      paymentIdentifiers: [],
    } as FormData,
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating custody account",
          description: parsed.error.issues[0]?.message,
        });
        return;
      }

      await createCustodyAccount({
        variables: {
          input: {
            productId: parsed.data.productId,
            currency: parsed.data.currency,
            clearingCustodyAccountId:
              parsed.data.clearingCustodyAccountId || null,
            transactionFeeRate: parsed.data.transactionFeeRate,
            paymentIdentifiers: parsed.data.paymentIdentifiers.map(
              formInputToGqlMutationInput,
            ),
          },
        },
      });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      form.reset();
    }
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button onClick={() => setOpen(true)}>New custody account</Button>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Create Custody Account</DialogTitle>
        </DialogHeader>

        <form.AppForm>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.setErrorMap({
                onSubmit: {
                  fields: {},
                },
              });
              void form.handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            <form.AppField name="productId">
              {(field) => (
                <field.SelectField label={<Txt variant="body">Product</Txt>}>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.internalName}
                    </SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="currency">
              {(field) => (
                <field.SelectField label={<Txt variant="body">Currency</Txt>}>
                  {currencyValues.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="clearingCustodyAccountId">
              {(field) => (
                <field.SelectField
                  label={
                    <Txt variant="body">
                      Clearing Custody Account (optional)
                    </Txt>
                  }
                  onValueChange={(value) => {
                    field.handleChange(value === "__NONE__" ? null : value);
                  }}
                >
                  <SelectItem value="__NONE__">— None —</SelectItem>
                  {custodyAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.shortRef}
                      {account.product
                        ? ` · ${account.product.internalName}`
                        : ""}
                    </SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="transactionFeeRate">
              {(field) => (
                <field.PercentageField
                  label="Transaction Fee Rate (optional)"
                  scale="0-1"
                  decimalScale={3}
                />
              )}
            </form.AppField>

            <EditPaymentIdentifiers
              form={form}
              fields={{ paymentIdentifiers: "paymentIdentifiers" }}
            />

            <div className="flex justify-end">
              <Button
                variant="primary"
                type="submit"
                loading={loading}
                disabled={loading}
              >
                Create custody account
              </Button>
            </div>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

const columns: ColumnDef<CustodyAccountRowFragment>[] = [
  {
    id: "shortRef",
    accessorFn: (row) => row.shortRef,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Short Ref" />
    ),
  },
  {
    id: "product",
    accessorFn: (row) => row.product?.internalName ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Product" />
    ),
    cell: ({ row }) => row.original.product?.internalName ?? "-",
  },
  {
    id: "currency",
    accessorFn: (row) => row.currency,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Currency" />
    ),
  },
  {
    id: "totalBalance",
    accessorFn: (row) => row.totalBalance?.amount?.toString() ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total Balance" />
    ),
    cell: ({ row }) => <CurrencyAmountUI {...row.original.totalBalance} />,
  },
  {
    id: "totalUnitBalance",
    accessorFn: (row) => row.totalUnitBalance?.amount?.toString() ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total Unit Balance" />
    ),
    cell: ({ row }) =>
      row.original.totalUnitBalance
        ? formatLongUnitAmount({ amount: row.original.totalUnitBalance.amount })
        : "-",
  },
  {
    id: "assignedBalance",
    accessorFn: (row) => row.assignedBalance?.amount?.toString() ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Assigned Balance" />
    ),
    cell: ({ row }) => <CurrencyAmountUI {...row.original.assignedBalance} />,
  },
  {
    id: "unassignedBalance",
    accessorFn: (row) => row.unassignedBalance?.amount?.toString() ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Unassigned Balance" />
    ),
    cell: ({ row }) => <CurrencyAmountUI {...row.original.unassignedBalance} />,
  },
];
